import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { parseAuraScore } from '../../utils/formatAuraScore';
import { installRequestId } from '../../utils/requestId';
import getAuraApiBaseUrl from '../../api/auraApiBase';

const AURA_API_URL = getAuraApiBaseUrl();

const auraApi = axios.create({
  baseURL: AURA_API_URL,
  withCredentials: true,
});
installRequestId(auraApi);

auraApi.interceptors.request.use((config) => {
  return config;
});

export const getAuraScore = createAsyncThunk(
  'aura/getScore',
  async (_, { rejectWithValue }) => {
    try {
      const response = await auraApi.get('/api/aura/score/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const initializeAura = createAsyncThunk(
  'aura/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const response = await auraApi.post('/api/aura/score/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const recordAuraAction = createAsyncThunk(
  'aura/recordAction',
  async (actionData, { rejectWithValue }) => {
    try {
      const response = await auraApi.post('/api/aura/action/', actionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const getAuraHistory = createAsyncThunk(
  'aura/getHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await auraApi.get('/api/aura/history/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const initialState = {
  score: null,
  level: null,
  actions: [],
  loading: false,
  error: null,
  milestoneReached: null,
};

const auraSlice = createSlice({
  name: 'aura',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMilestone: (state) => {
      state.milestoneReached = null;
    },
    resetAura: (state) => {
      state.score = null;
      state.level = null;
      state.actions = [];
      state.loading = false;
      state.error = null;
      state.milestoneReached = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAuraScore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAuraScore.fulfilled, (state, action) => {
        state.loading = false;
        state.score = parseAuraScore(action.payload?.score);
        state.level = action.payload.level;
      })
      .addCase(getAuraScore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(initializeAura.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeAura.fulfilled, (state, action) => {
        state.loading = false;
        state.score = parseAuraScore(action.payload?.score);
        state.level = action.payload.level;
      })
      .addCase(initializeAura.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(recordAuraAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recordAuraAction.fulfilled, (state, action) => {
        state.loading = false;
        const nextScore =
          action.payload.updatedScore?.score ?? action.payload.updated_aura?.score;
        const parsed = parseAuraScore(nextScore);
        if (parsed !== null) {
          state.score = parsed;
        }
        state.level = action.payload.updatedScore?.level ?? action.payload.updated_aura?.level ?? state.level;
        if (action.payload.action) {
          state.actions.unshift(action.payload.action);
        }
        if (action.payload.milestone_reached) {
          state.milestoneReached = {
            milestoneValue: action.payload.milestone_value,
            pointsAwarded: action.payload.points_awarded || 20,
            message: action.payload.message,
          };
        }
      })
      .addCase(recordAuraAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getAuraHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAuraHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.actions = action.payload;
      })
      .addCase(getAuraHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearMilestone, resetAura } = auraSlice.actions;
export default auraSlice.reducer;
